import { useState } from 'react';
import { Flex, Radio, Text } from '@mantine/core';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import type { KycFormAction } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/useTaxAndCompliance.types.ts';

const APPLY = 'apply';
const LEAVE = 'leave';

interface Props {
	action: KycFormAction | null;
	instanceCount: number | undefined;
	loading: boolean;
	onConfirm: (applyToInstances: boolean) => void;
	onClose: () => void;
}

/** Confirms a form archive/delete and asks whether it reaches the form's existing instances. */
export const KycFormActionDrawer = ({
	action,
	instanceCount,
	loading,
	onConfirm,
	onClose,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	// Defaults to applying, as before this option existed; the parent remounts it per form.
	const [choice, setChoice] = useState(APPLY);

	// Delete is the fallback copy so the drawer keeps its text while it animates closed.
	const mode = action ?? 'delete';

	return (
		<ConfirmDeleteDrawer
			opened={!!action}
			onClose={onClose}
			title={tMipManage(`settings.tax.form_action_drawer.${mode}.title`)}
			alertTitle={tMipManage(`settings.tax.form_action_drawer.${mode}.alert_title`)}
			alertDescription={tMipManage(
				`settings.tax.form_action_drawer.${mode}.alert_description`
			)}
			hideAlertIcon={true}
			confirmDeleteText={tMipManage(`settings.tax.form_action_drawer.${mode}.confirm_word`)}
			wordMismatchErrorMessage={tMipManage(
				`settings.tax.form_action_drawer.${mode}.word_mismatch`
			)}
			inputLabel={tMipManage('settings.tax.form_action_drawer.input_label')}
			inputPlaceholder={tMipManage('settings.tax.form_action_drawer.input_placeholder')}
			deleteBtnText={tMipManage(`settings.tax.form_action_drawer.${mode}.confirm_button`)}
			cancelBtnText={tMipManage('settings.tax.form_action_drawer.cancel_button')}
			deleteLoading={loading}
			handleDelete={() => onConfirm(choice === APPLY)}
		>
			{!!instanceCount && (
				<Flex direction="column" gap="0.75rem">
					<Text>
						{tMipManage('settings.tax.form_action_drawer.instance_count', {
							count: instanceCount,
						})}
					</Text>
					<Radio.Group
						label={tMipManage('settings.tax.form_action_drawer.instances_label')}
						value={choice}
						onChange={setChoice}
					>
						<Flex direction="column" gap="0.5rem" mt="0.5rem">
							<Radio
								value={APPLY}
								label={tMipManage(
									`settings.tax.form_action_drawer.${mode}.apply_to_instances`
								)}
							/>
							<Radio
								value={LEAVE}
								label={tMipManage(
									`settings.tax.form_action_drawer.${mode}.leave_instances`
								)}
							/>
						</Flex>
					</Radio.Group>
				</Flex>
			)}
		</ConfirmDeleteDrawer>
	);
};
